// "The shape of the desk" — the tickets tab's centrepiece (design handoff §4.2,
// prototype `Tickets B - Desk board.dc.html`).
//
// Pulse line · the two-band heatmap · the selection strip beneath it, which
// opens pre-selected on the hottest cell and is never blank.
//
// All maths lives in `deskModel.ts`; this file is paint only, and every colour
// comes from `brainTokens` — the handoff's hexes ARE those tokens
// (`#c23a3a` → dangerText, `#0f8f8f` → teal, `#b06a1a` → warnText, …), so the
// heatmap's `rgba(194,58,58,α)` is built by giving the TOKEN an alpha rather
// than pasting the literal.

import { useMemo } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import type { DeskState, DeskSummary, UrgentTicket } from "../lib/api";
import { PriorityPill, RegisterTable, RowAction, AssigneeMonogram, RefCell } from "./RegisterTable";
import type { RegisterTableColumn, RegisterTableRow, SpineTone } from "./RegisterTable";
import {
  DESK_BANDS,
  DESK_STATE_LABEL,
  DESK_STATE_TONE,
  buildAxes,
  buildMatrix,
  cellIntensity,
  cellTextIsWhite,
  deltaRender,
  formatDuration,
  formatAgo,
  hottestCell,
  isBallWithUs,
  selectionTickets,
  actionsForState,
  allColumnsLabel,
  deltaBaselineNote,
  type DeskAxis,
  type DeskSelection,
  type StateTone,
} from "./deskModel";

const c = brainTokens.color;

/** `#rrggbb` → `rgba(r,g,b,a)`. Keeps every colour bound to a token. */
export function tokenRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
}

const TONE_COLOR: Record<StateTone, string> = {
  danger: c.dangerText,
  warning: c.warnDot,
  teal: c.teal,
  neutral: c.placeholder,
  success: c.successDot,
};

const SPINE_FOR_STATE: Record<DeskState, SpineTone> = {
  needs_reply: "danger",
  needs_decision: "danger",
  untriaged: "warning",
  with_developers: "teal",
  waiting_on_customer: "neutral",
  ready_to_close: "success",
};

const MONO = brainTokens.font.mono;

function MonoMicro({
  children,
  color = c.faint,
  sx,
  title,
}: {
  children: React.ReactNode;
  color?: string;
  sx?: object;
  title?: string;
}) {
  return (
    <Box
      component="span"
      title={title}
      sx={{
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** The age chip: grey ≤7d, amber >7d, red >14d. */
export function AgeChip({ days, small }: { days: number; small?: boolean }) {
  const look =
    days > 14
      ? { bg: c.dangerBg, fg: c.dangerText }
      : days > 7
        ? { bg: c.warnBg, fg: c.warnText }
        : { bg: c.neutralPillBg, fg: c.muted };
  return (
    <Box
      component="span"
      sx={{
        fontFamily: MONO,
        fontSize: small ? 10.5 : 11,
        fontWeight: 700,
        padding: small ? "3px 7px" : "4px 9px",
        borderRadius: `${brainTokens.radius.pill}px`,
        whiteSpace: "nowrap",
        background: look.bg,
        color: look.fg,
      }}
    >
      {days}d
    </Box>
  );
}

/**
 * The verdict chip on a strip row / board card (D3). Absent verdict renders
 * nothing.
 *
 * The `AI ·` prefix is not decoration — it is the rule that everything
 * model-generated on this tab is VISIBLY a suggestion, never a fact the room
 * can mistake for a decision someone made. The side rail's "how to read it"
 * key carries the long-form of the same sentence, and the tooltip says it
 * outright.
 */
export function TriageChip({ ticket }: { ticket: UrgentTicket }) {
  if (!ticket.triage) return null;
  const pct = Math.round((ticket.triage.confidence ?? 0) * 100);
  const label = ticket.triage.disposition.replace(/_/g, " ");
  return (
    <Box
      component="span"
      title={`AI triage suggestion — not a decision${ticket.triage.stale ? " (stale: the ticket changed since it was scored)" : ""}. ${ticket.triage.recommendation}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: `${brainTokens.radius.pill}px`,
        background: c.tealTint2,
        color: c.tealInk,
        border: `1px solid ${c.tealTintBorder}`,
        whiteSpace: "nowrap",
        opacity: ticket.triage.stale ? 0.55 : 1,
      }}
    >
      ✦ AI · {label} · {pct}%
    </Box>
  );
}

export interface DeskHeatmapProps {
  tickets: UrgentTicket[];
  desk?: DeskSummary;
  fetchedAt?: number;
  /** The sweep hit Jira's page cap — say so; never imply a complete desk. */
  truncated?: boolean;
  /** Force this customer to have its own column (the side-rail pivot). */
  pinCustomer?: string | null;
  axis: DeskAxis;
  onAxisChange: (axis: DeskAxis) => void;
  /** null = "use the hottest cell" (the arrival state). */
  selection: DeskSelection | null;
  onSelectionChange: (selection: DeskSelection | null) => void;
  onOpenTicket: (key: string) => void;
  /** Rows already acted on this session: key -> confirmation text. */
  handled: Record<string, string>;
  onAct: (key: string, confirmation: string) => void;
}

export function DeskHeatmap({
  tickets,
  desk,
  fetchedAt,
  truncated,
  pinCustomer,
  axis,
  onAxisChange,
  selection,
  onSelectionChange,
  onOpenTicket,
  handled,
  onAct,
}: DeskHeatmapProps) {
  const axes = useMemo(
    () => buildAxes(tickets, { pinCustomer: pinCustomer ?? undefined }),
    [tickets, pinCustomer]
  );
  const axisDef = axes[axis];
  const matrix = useMemo(() => buildMatrix(tickets, axisDef), [tickets, axisDef]);
  const hottest = useMemo(() => hottestCell(tickets, axisDef, matrix), [tickets, axisDef, matrix]);

  // "Opens pre-selected on the hottest cell — never a blank state."
  const active = selection ?? hottest;
  const isDefaultSelection = selection === null;

  const nCols = axisDef.columns.length;
  const gridTemplate = `196px repeat(${nCols}, minmax(0,1fr)) 66px 62px`;

  const pulse = [
    { n: String(matrix.grandTotal), label: "open", color: c.ink },
    { n: String(desk?.withUs ?? matrix.bandTotals.us), label: "sitting with us", color: c.dangerText },
    { n: String(desk?.untriaged ?? matrix.rowTotals.untriaged ?? 0), label: "untriaged", color: c.warnText },
    { n: formatDuration(desk?.medianFirstResponseMs) ?? "—", label: "median first response", color: c.ink },
    {
      n: desk?.closedYesterday !== undefined ? String(desk.closedYesterday) : "—",
      label: "closed yesterday",
      color: c.successText,
    },
  ];

  return (
    <>
      {/* ---- Pulse line ---- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "30px",
          flexWrap: "wrap",
          padding: "14px 20px",
          background: c.pageBg,
          border: `1px solid ${c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
        }}
      >
        {pulse.map((p) => (
          <Box key={p.label} sx={{ display: "flex", alignItems: "baseline", gap: "9px" }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 500, color: p.color }}>
              {p.n}
            </Box>
            <Box component="span" sx={{ fontSize: 13.5, color: c.muted, whiteSpace: "nowrap" }}>
              {p.label}
            </Box>
          </Box>
        ))}
        {!!desk?.triagedPendingReview && (
          <Box component="span" sx={{ fontSize: 13, color: c.tealInk, whiteSpace: "nowrap" }}>
            {desk.triagedPendingReview} with AI suggestions
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {truncated && (
          <MonoMicro
            color={c.warnText}
            title="Jira returned more open tickets than the sweep's page cap — this heatmap is a partial desk."
            sx={{
              padding: "4px 9px",
              borderRadius: `${brainTokens.radius.pill}px`,
              background: c.warnBg,
              border: `1px solid ${c.warnBorder}`,
            }}
          >
            Partial — Jira page cap
          </MonoMicro>
        )}
        <MonoMicro color={c.muted2} sx={{ fontWeight: 500, letterSpacing: "0.09em", fontSize: 11.5 }}>
          Synced from Jira {formatAgo(fetchedAt)}
        </MonoMicro>
      </Box>

      {/* ---- Heatmap card ---- */}
      <Box
        sx={{
          background: c.pageBg,
          border: `1px solid ${c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
          mt: "14px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "14px 22px 4px",
            flexWrap: "wrap",
          }}
        >
          <MonoMicro>Split by</MonoMicro>
          <Box sx={{ display: "flex", background: c.borderLight2, borderRadius: `${brainTokens.radius.chip}px`, padding: "2px" }}>
            {(Object.keys(axes) as DeskAxis[]).map((k) => (
              <Box
                key={k}
                component="button"
                onClick={() => {
                  onAxisChange(k);
                  onSelectionChange(null);
                }}
                aria-pressed={axis === k}
                sx={{
                  fontFamily: brainTokens.font.sans,
                  fontSize: 12.5,
                  fontWeight: 600,
                  padding: "5px 13px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  border: "none",
                  whiteSpace: "nowrap",
                  background: axis === k ? c.pageBg : "transparent",
                  color: axis === k ? c.ink : c.muted,
                  boxShadow: axis === k ? brainTokens.shadow.card : "none",
                }}
              >
                {axes[k].label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ---- Grid ---- */}
        <Box sx={{ padding: "14px 22px 4px", overflowX: "auto" }}>
          <Box sx={{ minWidth: 720 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: gridTemplate, gap: "6px", alignItems: "center" }}>
              <span />
              {axisDef.columns.map((label, ci) => (
                <Box
                  key={label}
                  component="button"
                  onClick={() =>
                    onSelectionChange({ state: active?.state ?? DESK_BANDS[0].states[0], column: ci })
                  }
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.09em",
                    color: active?.column === ci ? c.ink : c.muted2,
                    background: "transparent",
                    border: "none",
                    padding: "4px 0",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  title={`Select the ${label} column`}
                >
                  {label}
                </Box>
              ))}
              <MonoMicro color={c.muted2} sx={{ textAlign: "right", justifySelf: "end" }}>
                Total
              </MonoMicro>
              <MonoMicro
                color={c.muted2}
                sx={{ textAlign: "right", justifySelf: "end" }}
                title={deltaBaselineNote(desk?.baselineDate, desk?.baselineAt, !!desk?.deltas)}
              >
                Δ 1D
              </MonoMicro>
            </Box>

            {DESK_BANDS.map((band) => (
              <Box key={band.key} sx={{ mt: "10px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "6px" }}>
                  <MonoMicro color={band.key === "us" ? c.dangerText : c.tealInk} sx={{ letterSpacing: "0.11em" }}>
                    {band.label}
                  </MonoMicro>
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: 11.5, color: c.muted2 }}>
                    {matrix.bandTotals[band.key]} tickets
                  </Box>
                  <Box sx={{ flex: 1, height: "1px", background: c.borderLight2 }} />
                </Box>

                {band.states.map((state) => {
                  const rowSelected = active?.state === state && active?.column === null;
                  const d = deltaRender(state, desk?.deltas?.[state]);
                  return (
                    <Box
                      key={state}
                      sx={{ display: "grid", gridTemplateColumns: gridTemplate, gap: "6px", alignItems: "center", mt: "6px" }}
                    >
                      <Box
                        component="button"
                        onClick={() =>
                          onSelectionChange(rowSelected ? null : { state, column: null })
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "9px",
                          minWidth: 0,
                          padding: "6px 10px 6px 4px",
                          borderRadius: `${brainTokens.radius.button}px`,
                          cursor: "pointer",
                          textAlign: "left",
                          fontFamily: brainTokens.font.sans,
                          background: rowSelected ? c.panelBg2 : "transparent",
                          border: `1px solid ${rowSelected ? c.hoverBorder : "transparent"}`,
                          color: c.ink2,
                        }}
                      >
                        <Box
                          aria-hidden
                          sx={{ width: 9, height: 9, borderRadius: "50%", flex: "none", background: TONE_COLOR[DESK_STATE_TONE[state]] }}
                        />
                        <Box
                          component="span"
                          sx={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {DESK_STATE_LABEL[state]}
                        </Box>
                      </Box>

                      {matrix.cells[state].map((count, ci) => {
                        const hot = isBallWithUs(state);
                        const intensity = cellIntensity({
                          count,
                          columnIndex: ci,
                          columnCount: nCols,
                          maxCell: matrix.maxCell,
                          ballWithUs: hot,
                          weighted: axisDef.weighted,
                        });
                        const on = active?.state === state && active?.column === ci;
                        const background =
                          count === 0
                            ? c.panelBg
                            : hot
                              ? tokenRgba(c.dangerText, intensity)
                              : tokenRgba(c.teal, intensity * 0.5);
                        return (
                          <Box
                            key={ci}
                            component="button"
                            disabled={count === 0}
                            onClick={() => onSelectionChange(on ? null : { state, column: ci })}
                            aria-label={`${DESK_STATE_LABEL[state]}, ${axisDef.columns[ci]}: ${count}`}
                            sx={{
                              fontFamily: MONO,
                              fontSize: 15,
                              fontWeight: 500,
                              padding: "13px 0",
                              borderRadius: `${brainTokens.radius.button}px`,
                              cursor: count ? "pointer" : "default",
                              background,
                              border: on
                                ? `2px solid ${c.ink}`
                                : `1px solid ${count === 0 ? c.borderLight2 : "transparent"}`,
                              color:
                                count === 0
                                  ? c.placeholder
                                  : cellTextIsWhite(intensity, hot)
                                    ? c.white
                                    : c.ink2,
                              transition: "background 120ms ease",
                              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                            }}
                          >
                            {count}
                          </Box>
                        );
                      })}

                      <Box sx={{ fontFamily: MONO, fontSize: 15, color: c.ink2, textAlign: "right" }}>
                        {matrix.rowTotals[state]}
                      </Box>
                      <Box
                        title={deltaBaselineNote(desk?.baselineDate, desk?.baselineAt, !!desk?.deltas)}
                        sx={{
                          fontFamily: MONO,
                          fontSize: 12,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          color:
                            d.tone === "danger"
                              ? c.dangerText
                              : d.tone === "success"
                                ? c.successText
                                : d.tone === "muted"
                                  ? c.muted
                                  : c.placeholder,
                        }}
                      >
                        {d.text}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: gridTemplate,
                gap: "6px",
                alignItems: "center",
                mt: "12px",
                pt: "10px",
                borderTop: `1px solid ${c.borderLight2}`,
              }}
            >
              <MonoMicro color={c.muted2}>Column</MonoMicro>
              {matrix.columnTotals.map((n, i) => (
                <Box key={i} sx={{ fontFamily: MONO, fontSize: 13, color: c.muted, textAlign: "center" }}>
                  {n}
                </Box>
              ))}
              <Box sx={{ fontFamily: MONO, fontSize: 15, color: c.ink, textAlign: "right" }}>
                {matrix.grandTotal}
              </Box>
              <span />
            </Box>
          </Box>
        </Box>

        {/* ---- Selection strip ---- */}
        <SelectionStrip
          tickets={tickets}
          axisDef={axisDef}
          selection={active}
          isDefault={isDefaultSelection}
          matrix={matrix}
          onClear={() => onSelectionChange(null)}
          onOpenTicket={onOpenTicket}
          handled={handled}
          onAct={onAct}
        />
      </Box>
    </>
  );
}

const STRIP_COLUMNS: RegisterTableColumn[] = [
  { key: "priority", label: "", width: "52px" },
  { key: "ref", label: "Ref", width: "104px" },
  { key: "summary", label: "Summary", width: "minmax(200px,1fr)" },
  { key: "customer", label: "Customer", width: "140px", collapse: true },
  { key: "who", label: "", width: "28px", align: "center" },
  { key: "age", label: "Idle", width: "62px" },
  { key: "actions", label: "", width: "230px", align: "end" },
];

function SelectionStrip({
  tickets,
  axisDef,
  selection,
  isDefault,
  matrix,
  onClear,
  onOpenTicket,
  handled,
  onAct,
}: {
  tickets: UrgentTicket[];
  axisDef: ReturnType<typeof buildAxes>[DeskAxis];
  selection: DeskSelection | null;
  isDefault: boolean;
  matrix: ReturnType<typeof buildMatrix>;
  onClear: () => void;
  onOpenTicket: (key: string) => void;
  handled: Record<string, string>;
  onAct: (key: string, confirmation: string) => void;
}) {
  if (!selection) {
    return (
      <Box sx={{ borderTop: `1px solid ${c.borderLight2}`, background: c.panelBg, padding: "22px", textAlign: "center", fontSize: 13.5, color: c.faint }}>
        Nothing open on the desk. Nothing to work.
      </Box>
    );
  }

  const ballWithUs = isBallWithUs(selection.state);
  const matched = selectionTickets(tickets, axisDef, selection);
  const count =
    selection.column === null
      ? matrix.rowTotals[selection.state]
      : matrix.cells[selection.state][selection.column];
  const shown = matched.slice(0, 5);
  const actions = actionsForState(selection.state);

  const rows: RegisterTableRow[] = shown.map((t) => {
    const done = handled[t.key];
    return {
      id: t.key,
      spine: SPINE_FOR_STATE[t.deskState ?? "needs_reply"],
      faded: !!done,
      onClick: () => onOpenTicket(t.key),
      title: `Open ${t.key}`,
      cells: {
        priority: <PriorityPill priority={t.bucket} />,
        ref: <RefCell label={t.key} href={t.url} />,
        summary: (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Box
              component="span"
              title={t.summary}
              sx={{ fontSize: 14.5, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {t.summary}
            </Box>
            <TriageChip ticket={t} />
          </Box>
        ),
        customer: (
          <Box component="span" sx={{ fontSize: 13, color: c.ink4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.customer ?? t.account ?? "—"}
          </Box>
        ),
        who: <AssigneeMonogram name={t.assigneeName} size={21} />,
        age: <AgeChip days={t.idleDays ?? 0} />,
        actions: (
          <RowAction>
            {done ? (
              <Box
                component="span"
                title="Marked handled in this standup session — a personal checklist marker, not a Jira write."
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: `${brainTokens.radius.button}px`,
                  background: c.successBg,
                  color: c.successText,
                  border: `1px solid ${c.successBorder}`,
                  whiteSpace: "nowrap",
                }}
              >
                {done}
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: "7px" }}>
                {actions.map((a) => (
                  <Box
                    key={a.label}
                    component="button"
                    onClick={() => onAct(t.key, a.done)}
                    sx={{
                      fontFamily: brainTokens.font.sans,
                      fontSize: 12.5,
                      fontWeight: a.primary ? 600 : 500,
                      padding: "6px 12px",
                      borderRadius: `${brainTokens.radius.button}px`,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      background: a.primary ? c.teal : c.pageBg,
                      color: a.primary ? c.white : c.ink3,
                      border: `1px solid ${a.primary ? c.teal : c.dropBorder}`,
                    }}
                  >
                    {a.label}
                  </Box>
                ))}
              </Box>
            )}
          </RowAction>
        ),
      },
    };
  });

  return (
    <Box sx={{ borderTop: `1px solid ${c.borderLight2}`, background: ballWithUs ? c.dangerBg2 : c.panelBg }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 22px 12px", flexWrap: "wrap" }}>
        <MonoMicro
          color={ballWithUs ? c.dangerText : c.tealInk}
          sx={{
            padding: "5px 10px",
            borderRadius: `${brainTokens.radius.pill}px`,
            background: ballWithUs ? c.dangerBg : c.tealTint,
          }}
        >
          {ballWithUs ? "Ball with us" : "Moving without us"}
        </MonoMicro>
        <Box component="span" sx={{ fontSize: 15, fontWeight: 600, color: c.ink }}>
          {DESK_STATE_LABEL[selection.state]}
          {selection.column === null
            ? ` · ${allColumnsLabel(axisDef.key)}`
            : ` · ${axisDef.columns[selection.column]}`}
        </Box>
        <Box component="span" sx={{ fontSize: 13.5, color: c.muted }}>
          {count} {count === 1 ? "ticket" : "tickets"}
          {isDefault ? " — the hottest cell, selected for you" : ""}
        </Box>
        <Box sx={{ flex: 1 }} />
        {!isDefault && (
          <Box
            component="button"
            onClick={onClear}
            sx={{
              fontFamily: brainTokens.font.sans,
              fontSize: 12.5,
              fontWeight: 600,
              color: c.ink3,
              background: c.pageBg,
              border: `1px solid ${c.dropBorder}`,
              borderRadius: `${brainTokens.radius.pill}px`,
              padding: "5px 12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Clear selection
          </Box>
        )}
      </Box>

      <Box sx={{ px: "10px", pb: "6px" }}>
        <RegisterTable
          columns={STRIP_COLUMNS}
          rows={rows}
          dense
          empty="No sample rows loaded for this cell."
        />
      </Box>

      {count > shown.length && (
        <Box sx={{ padding: "11px 22px 14px", fontSize: 13, color: c.muted2 }}>
          + {count - shown.length} more in this cell
        </Box>
      )}
    </Box>
  );
}
